import type {
  ProductLayoutNode,
  ProductLayoutBlockNode,
  ProductLayoutFieldNode,
  ProductLayoutTree,
} from 'models/forms/FormResult';
import { LayoutCustomizationBehavior } from 'models/metadata/shared.definition';
import { QuoteFormError } from 'state/addonForm/addonForm.selector';
import { ProductNode } from 'state/product-description/model';
import { ProductLayoutOverride } from 'state/userPreferences/userPreferences.model';

const validationErrorsFieldIdsWithoutMatchingNodes = ['CustomizedScheduleDates'];

export function isProductLayoutBlockNode(node: ProductLayoutNode): node is ProductLayoutBlockNode {
  return node.kind === 'Block';
}

export function isProductLayoutFieldNode(node: ProductLayoutNode): node is ProductLayoutFieldNode {
  return node.kind === 'Field';
}

export function getProductLayoutNodeCustomizationBehavior(
  node: ProductLayoutNode,
): LayoutCustomizationBehavior | undefined {
  if (isProductLayoutBlockNode(node)) {
    return node.definition.layoutCustomizationBehavior;
  }
  if (isProductLayoutFieldNode(node)) {
    return node.definition.layoutCustomizationBehavior;
  }
  return undefined;
}
export function isHiddenNode(node: ProductLayoutNode): boolean {
  const layoutCustomizationBehavior = getProductLayoutNodeCustomizationBehavior(node);
  return layoutCustomizationBehavior === 'Hidden';
}

export function collectFieldIds(node: ProductNode): string[] {
  if (!node?.children?.length) {
    return [node.id];
  }

  return node.children.flatMap(collectFieldIds);
}

export function collectFieldIdsFromProductLayout(childrens: ProductLayoutNode[]): string[] {
  const ids: string[] = [];

  for (const child of childrens) {
    if (isProductLayoutBlockNode(child)) {
      ids.push(...collectFieldIdsFromProductLayout(child.childrens));
    } else {
      ids.push(child.id);
    }
  }

  return ids;
}

export function getValidationOfNode(item: ProductNode, validationErrors: QuoteFormError[]) {
  const fieldIds = collectFieldIds(item);
  const errors: QuoteFormError[] = [];

  for (const err of validationErrors) {
    if (!err.fields) {
      continue;
    }

    if (
      err.fields.some(f => fieldIds.includes(f)) ||
      err.fields.some(f => validationErrorsFieldIdsWithoutMatchingNodes.includes(f))
    ) {
      errors.push(err);
    }
  }

  return errors;
}

export function getValidationOfProductLayoutNode(
  blockId: string,
  childrens: ProductLayoutNode[],
  validationErrors: QuoteFormError[],
) {
  const fieldIds = [blockId, ...collectFieldIdsFromProductLayout(childrens)];
  const errors: QuoteFormError[] = [];

  for (const err of validationErrors) {
    if (!err.fields) {
      continue;
    }

    if (
      err.fields.some(f => fieldIds.includes(f)) ||
      err.fields.some(f => validationErrorsFieldIdsWithoutMatchingNodes.includes(f))
    ) {
      errors.push(err);
    }
  }

  return errors;
}

export function containsAdvancedFieldInUserPreferences(
  blockNode: ProductLayoutBlockNode,
  additionalConfigFields: Set<string> | undefined,
): boolean {
  return blockNode.childrens
    .filter(isProductLayoutFieldNode)
    .some(child => additionalConfigFields?.has(child.id) ?? false);
}

export function reOrderChildrenInNode(
  node: ProductLayoutNode,
  orderedFieldIds: Map<string, number>,
): ProductLayoutNode {
  if (!isProductLayoutBlockNode(node)) {
    return node;
  }

  const reorderedChildrens = node.childrens.map(child => reOrderChildrenInNode(child, orderedFieldIds));

  const customizableChildrens = reorderedChildrens.filter(child => isProductLayoutFieldNode(child));

  return {
    ...node,
    childrens: [
      ...customizableChildrens.sort((a, b) => {
        const orderA = orderedFieldIds.get(a.id) ?? Number.MAX_SAFE_INTEGER;
        const orderB = orderedFieldIds.get(b.id) ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      }),
    ],
  };
}

export function applyCustomLayoutPreferences(
  productLayout: ProductLayoutTree,
  preferences: ProductLayoutOverride | undefined,
): ProductLayoutTree {
  const orderedByFieldId = new Map<string, number>();

  [...(preferences?.visibleFields ?? []), ...(preferences?.additionalFields ?? [])].forEach((fieldId, index) => {
    orderedByFieldId.set(fieldId, index);
  });
  return {
    ...productLayout,
    nodes: productLayout.nodes.map(node => reOrderChildrenInNode(node, orderedByFieldId)),
  };
}
